import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  useTheme,
  CircularProgress,
} from "@mui/material";
import useTimer from "./useTimer";
import { PropsWithChildren, useState } from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import TimerIcon from "@mui/icons-material/Timer";
import { useNavigate } from "react-router-dom";
import { getRoundTitleText } from "../../utils/utils";
import { NavigateBefore, NavigateNext } from "@mui/icons-material";
import { StandardButton } from "../../components/StandardButton";
import NumberInput from "../../components/NumberInput";
import { numberInputDarkSx } from "../game-builder/ClosestGuessEditor";

export const ControlCenterRoundContainer: React.FC<
  PropsWithChildren<{
    onClickPrevious: () => Promise<void>;
    onClickNext: () => Promise<void>;
    currentRoundIdx: number | "bonus" | "overtime" | null;
    category?: string;
  }>
> = ({ children, onClickNext, onClickPrevious, currentRoundIdx, category }) => {
  const navigate = useNavigate();

  const theme = useTheme();

  const [isSwitchingRounds, setIsSwitchingRounds] = useState(false);

  const handleNextRound = async () => {
    setIsSwitchingRounds(true);
    await onClickNext();
    setIsSwitchingRounds(false);
  };

  const handlePreviousRound = async () => {
    setIsSwitchingRounds(true);
    await onClickPrevious();
    setIsSwitchingRounds(false);
  };

  const onTimerEnd = () => {};

  const { secondsLeft, setTime, clear, pause, isActive, start } =
    useTimer(onTimerEnd);

  const onChangeTimerInput = (n: number) => {
    setTime(n);
    pause();
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "auto",
        flex: 1,
        alignItems: "center",
        width: "100vw",
        gap: 1,
        px: 2,
        py: 1,
        backgroundColor: theme.palette.primary.dark,
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Box display="flex">
          <StandardButton
            onClick={() => navigate("/admin")}
            variant="contained"
            color="primary"
            startIcon={<NavigateBefore />}
          >
            Back to Dashboard
          </StandardButton>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            backgroundColor: theme.palette.primary.dark,
            border: "1px solid",
            borderColor: theme.palette.primary.main,
            borderRadius: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: 600,
            gap: 4,
          }}
        >
          <IconButton
            onClick={handlePreviousRound}
            size="large"
            color="primary"
            disabled={currentRoundIdx === null || isSwitchingRounds}
            data-testid="prev-round-btn"
            sx={{
              bgcolor: "rgba(100, 116, 139, 0.08)",
              "&:hover": {
                bgcolor: "rgba(100, 116, 139, 0.15)",
              },
              "&:disabled": {
                bgcolor: "transparent",
              },
            }}
          >
            <NavigateBefore />
          </IconButton>

          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="overline"
              color="primary"
              sx={{ display: "block", mb: 0.5 }}
            >
              Current Round
            </Typography>
            <Typography variant="h4" fontWeight="600" color="primary">
              {getRoundTitleText(currentRoundIdx, category)}
            </Typography>
          </Box>

          <IconButton
            onClick={handleNextRound}
            size="large"
            color="primary"
            disabled={currentRoundIdx === "overtime" || isSwitchingRounds}
            data-testid="next-round-btn"
            sx={{
              bgcolor: "rgba(100, 116, 139, 0.08)",
              "&:hover": {
                bgcolor: "rgba(100, 116, 139, 0.15)",
              },
              "&:disabled": {
                bgcolor: "transparent",
              },
            }}
          >
            {currentRoundIdx === 11 ? (
              <Typography variant="button" sx={{ px: 2 }}>
                Overtime
              </Typography>
            ) : (
              <NavigateNext />
            )}
          </IconButton>
        </Paper>

        <Box display="flex" width="188px" />
      </Box>

      {/* Timer Controls */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          backgroundColor: theme.palette.primary.dark,
          border: "1px solid",
          borderColor: theme.palette.primary.main,
          borderRadius: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              height: "100%",
            }}
          >
            <NumberInput
              label="Seconds"
              value={secondsLeft}
              onChange={(e) => onChangeTimerInput(e ?? 0)}
              size="small"
              sx={{ width: 120, ...numberInputDarkSx }}
            />

            <Box
              sx={{
                display: "flex",
                gap: 1,
                mt: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <StandardButton
                onClick={() => {
                  setTime(10);
                  start();
                }}
                variant="outlined"
                size="small"
              >
                10s
              </StandardButton>
              <StandardButton
                onClick={() => {
                  setTime(20);
                  start();
                }}
                variant="outlined"
                size="small"
              >
                20s
              </StandardButton>
              <StandardButton
                onClick={() => {
                  setTime(35);
                  start();
                }}
                variant="outlined"
                size="small"
              >
                35s
              </StandardButton>
            </Box>

            <Tooltip title={isActive ? "Pause" : "Start"}>
              <IconButton
                onClick={isActive ? pause : start}
                sx={{
                  bgcolor: isActive ? "warning.main" : "success.main",
                  color: "white",
                  "&:hover": {
                    bgcolor: isActive ? "warning.dark" : "success.dark",
                  },
                }}
                size="large"
              >
                {isActive ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset">
              <IconButton
                onClick={clear}
                sx={{
                  bgcolor: "error.main",
                  color: "white",
                  "&:hover": {
                    bgcolor: "error.dark",
                  },
                }}
                size="large"
              >
                <TimerIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Box
              sx={{
                px: 4,
                py: 2,
                borderRadius: 1,
                bgcolor: isActive
                  ? "rgba(5, 150, 105, 0.1)"
                  : "rgba(100, 116, 139, 0.08)",
                border: "2px solid",
                borderColor: isActive ? "success.main" : "divider",
                transition: "all 0.3s ease",
                minWidth: 140,
                textAlign: "center",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "monospace",
                  fontWeight: "700",
                  color: isActive ? "success.main" : "text.primary",
                }}
              >
                {formatTime(secondsLeft)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      <Box display="flex" width="100%" flex={1} justifyContent="center">
        {isSwitchingRounds ? <CircularProgress /> : children}
      </Box>
    </Box>
  );
};
